/**
 * The LookBook — interactive magazine page (templates/page.lookbook.json).
 *
 * One scroll engine drives every section: elements marked data-lb-progress
 * get a --p custom property (0 → 1) once per animation frame while they are
 * on screen —
 *   - "view": how far the element has travelled through the viewport;
 *   - "pin":  how far through its own pinned length (height minus one screen).
 * CSS turns --p into transforms (assets/lookbook-magazine.css); components
 * that need discrete steps (the "up close" frames) also get a callback.
 *
 * Also here: reveal-on-scroll, the chapter rail, page numbers ("folios"),
 * smooth in-page jumps, the contents hover preview, the compare slider and
 * the buy boxes' size pills.
 *
 * Motion is only switched on (html.lb-motion) when the visitor has not asked
 * for reduced motion; without it every section renders static and complete.
 */
(() => {
  if (window.__lookbookMagazine) return;
  window.__lookbookMagazine = true;

  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const motion = () => !reduceMotion.matches;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  root.classList.add('lb-ready');
  if (motion()) root.classList.add('lb-motion');

  /* ---------- Scroll engine ---------- */

  const tracked = new Set();
  let viewportHeight = window.innerHeight;
  let ticking = false;

  function progressOf(el, rect) {
    if (el.dataset.lbProgress === 'pin') {
      const distance = rect.height - viewportHeight;
      return distance > 0 ? clamp(-rect.top / distance) : 0;
    }
    return clamp((viewportHeight - rect.top) / (viewportHeight + rect.height));
  }

  function update() {
    ticking = false;
    if (!motion()) return;

    tracked.forEach((el) => {
      const rect = el.getBoundingClientRect();
      // Off screen: settle at whichever end it left from, then skip.
      const offscreen = rect.bottom < 0 || rect.top > viewportHeight;
      const p = offscreen ? (rect.bottom < 0 ? 1 : 0) : progressOf(el, rect);
      if (el.__lbP === p) return;
      el.__lbP = p;
      el.style.setProperty('--p', p.toFixed(4));
      if (typeof el.onLookbookProgress === 'function') el.onLookbookProgress(p);
    });

    Rail.update();
  }

  function requestUpdate() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  }

  function measure() {
    viewportHeight = window.innerHeight;
    tracked.forEach((el) => {
      if (typeof el.onLookbookResize === 'function') el.onLookbookResize();
    });
    requestUpdate();
  }

  /* ---------- Reveal on scroll ---------- */

  const revealObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -6% 0px' }
  );

  /* ---------- Folios and the contents' page numbers ---------- */

  function numberPages() {
    const pages = Array.from(document.querySelectorAll('[data-lb-page]'));
    const pad = (n) => String(n).padStart(2, '0');
    const write = (el, text) => {
      if (el.textContent !== text) el.textContent = text;
    };
    pages.forEach((page, index) => {
      page.dataset.lbPageNumber = pad(index + 1);
      page.querySelectorAll(':scope > .lb-folio [data-lb-folio]').forEach((folio) => write(folio, `P. ${pad(index + 1)}`));
    });
    document.querySelectorAll('[data-lb-page-of]').forEach((slot) => {
      const target = document.getElementById(slot.dataset.lbPageOf);
      const page = target && target.closest('[data-lb-page]');
      write(slot, page ? `P. ${page.dataset.lbPageNumber}` : '');
    });
  }

  /* ---------- Smooth in-page jumps ---------- */

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-lb-jump]');
    if (!link) return;
    const id = decodeURIComponent(link.hash.slice(1));
    const target = id && document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: motion() ? 'smooth' : 'auto', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  });

  /* ---------- Chapter rail ---------- */

  const Rail = {
    nav: null,
    chapters: [],
    links: [],
    active: -1,

    build() {
      this.chapters = Array.from(document.querySelectorAll('[data-lb-chapter][id]'));
      if (!this.chapters.length) return;

      this.nav = document.createElement('nav');
      this.nav.className = 'lb-rail';
      this.nav.setAttribute('aria-label', 'Chapters');
      const list = document.createElement('ol');
      this.chapters.forEach((chapter) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        link.href = `#${chapter.id}`;
        link.dataset.lbJump = '';
        const num = document.createElement('span');
        num.className = 'lb-rail__num';
        num.textContent = chapter.dataset.lbChapterNumber || '';
        const label = document.createElement('span');
        label.className = 'lb-rail__label';
        label.textContent = chapter.dataset.lbChapterTitle || '';
        link.append(num, label);
        item.append(link);
        list.append(item);
        this.links.push(item);
      });
      this.nav.append(list);
      document.body.append(this.nav);
      this.update();
    },

    update() {
      if (!this.nav) return;
      // The current chapter is the last one whose top has passed 40% down
      // the screen. Before the first chapter (cover, contents) the rail hides.
      const line = viewportHeight * 0.4;
      let active = -1;
      this.chapters.forEach((chapter, index) => {
        if (chapter.getBoundingClientRect().top <= line) active = index;
      });
      // ...and it hides again once the last chapter has scrolled away.
      const last = this.chapters[this.chapters.length - 1];
      const past = last.closest('.shopify-section') || last;
      if (active === this.chapters.length - 1 && past.getBoundingClientRect().bottom < line) active = -1;

      if (active === this.active) return;
      this.active = active;
      this.nav.classList.toggle('is-visible', active > -1);
      this.links.forEach((item, index) => {
        item.classList.toggle('is-active', index === active);
        const link = item.querySelector('a');
        if (index === active) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    },
  };

  /* ---------- Cover ---------- */

  class LookbookCover extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('.lb-cover__card');
      this.stage = this.querySelector('.lb-cover__stage');
      this.onLookbookResize();
    }

    onLookbookResize() {
      if (!this.card || !this.stage) return;
      // The site header (and any announcement bar) sits above the cover on
      // first load; the cover is sized to fit under it.
      const main = document.getElementById('MainContent');
      const above = main ? main.getBoundingClientRect().top + window.scrollY : 0;
      root.style.setProperty('--lb-header', `${Math.max(0, Math.round(above))}px`);

      // Opening moves the cover from where it sits to the middle of the
      // stage, and grows it until it covers the stage.
      const cardCentre = this.card.offsetTop + this.card.offsetHeight / 2;
      this.style.setProperty('--dy', `${(this.stage.clientHeight / 2 - cardCentre).toFixed(1)}px`);
      const fill = Math.max(
        this.stage.clientWidth / this.card.offsetWidth,
        this.stage.clientHeight / this.card.offsetHeight
      );
      this.style.setProperty('--fill', (fill * 1.02).toFixed(3));
    }
  }

  /* ---------- Contents: floating preview ---------- */

  class LookbookContents extends HTMLElement {
    connectedCallback() {
      this.preview = this.querySelector('.lb-contents__preview');
      this.images = this.preview ? Array.from(this.preview.querySelectorAll('img, svg')) : [];
      const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
      if (!this.preview || !fine.matches) return;

      this.target = { x: 0, y: 0 };
      this.current = { x: 0, y: 0 };

      this.querySelectorAll('[data-preview-index]').forEach((entry) => {
        entry.addEventListener('pointerenter', (event) => {
          const index = Number(entry.dataset.previewIndex);
          this.images.forEach((image, i) => image.classList.toggle('is-active', i === index));
          if (!this.classList.contains('is-previewing')) {
            // Start where the pointer is, rather than sliding in from 0,0.
            this.setTarget(event);
            this.current = { ...this.target };
          }
          this.classList.add('is-previewing');
          this.follow();
        });
      });

      const list = this.querySelector('.lb-contents__list');
      list.addEventListener('pointermove', (event) => this.setTarget(event));
      list.addEventListener('pointerleave', () => this.classList.remove('is-previewing'));
    }

    setTarget(event) {
      // Sit beside the pointer, not under it, so the entry stays readable.
      this.target.x = event.clientX + 32;
      this.target.y = event.clientY - this.preview.offsetHeight / 2;
    }

    // Ease toward the pointer each frame while previewing — a soft follow.
    follow() {
      if (this.following) return;
      this.following = true;
      const step = () => {
        const ease = motion() ? 0.16 : 1;
        this.current.x += (this.target.x - this.current.x) * ease;
        this.current.y += (this.target.y - this.current.y) * ease;
        this.preview.style.translate = `${this.current.x.toFixed(1)}px ${this.current.y.toFixed(1)}px`;
        if (this.classList.contains('is-previewing')) requestAnimationFrame(step);
        else this.following = false;
      };
      requestAnimationFrame(step);
    }
  }

  /* ---------- Up close: frames stepped by scroll, markers that shop ---------- */

  class LookbookDetail extends HTMLElement {
    connectedCallback() {
      this.frames = Array.from(this.querySelectorAll('[data-frame]'));
      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.counter = this.querySelector('[data-lb-detail-current]');
      this.index = 0;
      this.openCard = null;
      this.bindMarkers();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onOutsideClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('scroll', this.onScroll);
      if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
    }

    onLookbookProgress(p) {
      const count = this.frames.length;
      if (!count) return;
      const index = Math.min(count - 1, Math.floor(p * count));
      if (index === this.index) return;
      this.index = index;
      // The card belongs to the frame that just left.
      this.closeCard();
      this.frames.forEach((frame, i) => {
        frame.classList.toggle('is-active', i === index);
        frame.classList.toggle('is-past', i < index);
      });
      this.steps.forEach((step, i) => step.classList.toggle('is-active', i === index));
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
    }

    /* A marker opens its card on hover with a mouse, and on click or tap
       (which also pins it open). Escape, a click elsewhere, the close button
       or moving on to the next frame closes it. */
    bindMarkers() {
      const hover = window.matchMedia('(hover: hover) and (pointer: fine)');

      this.querySelectorAll('[data-lb-marker]').forEach((marker) => {
        const card = document.getElementById(marker.getAttribute('aria-controls'));
        if (!card) return;
        marker.lbCard = card;
        card.lbMarker = marker;

        marker.addEventListener('click', (event) => {
          event.stopPropagation();
          if (this.openCard === card && card.dataset.pinned === 'true') {
            this.closeCard({ returnFocus: false });
            return;
          }
          this.showCard(marker, { pinned: true, focus: event.detail === 0 });
        });

        if (hover.matches) {
          marker.addEventListener('pointerenter', () => {
            clearTimeout(this.hideTimer);
            if (this.openCard !== card) this.showCard(marker, { pinned: false });
          });
          marker.addEventListener('pointerleave', () => this.scheduleHide(card));
          card.addEventListener('pointerenter', () => clearTimeout(this.hideTimer));
          card.addEventListener('pointerleave', () => this.scheduleHide(card));
        }

        card.querySelector('[data-lb-card-close]').addEventListener('click', () => this.closeCard());
      });

      this.onOutsideClick = (event) => {
        if (!this.openCard) return;
        if (this.openCard.contains(event.target) || event.target.closest('[data-lb-marker]')) return;
        this.closeCard({ returnFocus: false });
      };
      document.addEventListener('click', this.onOutsideClick);

      this.onKeydown = (event) => {
        if (event.key === 'Escape' && this.openCard) this.closeCard();
      };
      document.addEventListener('keydown', this.onKeydown);

      // Once the piece is in the bag, step aside for the cart notification.
      // (PUB_SUB_EVENTS is a top-level const in constants.js — global, but not on window.)
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.closeCard({ returnFocus: false }));
      }

      // In the static layout the marker scrolls with the page; keep the card on it.
      this.onScroll = () => {
        if (this.openCard) this.placeCard(this.openCard);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }

    scheduleHide(card) {
      if (card.dataset.pinned === 'true') return;
      clearTimeout(this.hideTimer);
      this.hideTimer = setTimeout(() => {
        if (this.openCard === card && card.dataset.pinned !== 'true') this.closeCard({ returnFocus: false });
      }, 220);
    }

    showCard(marker, { pinned = false, focus = false } = {}) {
      const card = marker.lbCard;
      if (this.openCard && this.openCard !== card) this.closeCard({ returnFocus: false });

      this.openCard = card;
      card.dataset.pinned = String(pinned);
      card.hidden = false;
      marker.setAttribute('aria-expanded', 'true');
      this.placeCard(card);
      requestAnimationFrame(() => card.classList.add('is-open'));

      // Opened from the keyboard: take focus into the card so its sizes and
      // Add to cart are the next things reached.
      if (focus) {
        const first = card.querySelector('.lb-buy__pill input:checked, .lb-buy__pill input:not(:disabled), .lb-buy__add');
        requestAnimationFrame(() => (first || card).focus({ preventScroll: true }));
      }
    }

    closeCard({ returnFocus = true } = {}) {
      const card = this.openCard;
      if (!card) return;
      clearTimeout(this.hideTimer);
      this.openCard = null;
      card.classList.remove('is-open');
      card.dataset.pinned = 'false';
      const marker = card.lbMarker;
      marker.setAttribute('aria-expanded', 'false');
      if (returnFocus && card.contains(document.activeElement)) marker.focus({ preventScroll: true });
      setTimeout(() => {
        if (this.openCard !== card) card.hidden = true;
      }, 260);
    }

    // Beside the marker, on whichever side has room; phones use the bottom sheet.
    placeCard(card) {
      if (window.matchMedia('(max-width: 749px)').matches) {
        card.style.left = '';
        card.style.top = '';
        return;
      }
      const marker = card.lbMarker.getBoundingClientRect();
      const gap = 20;
      const margin = 16;
      const width = card.offsetWidth;
      const height = card.offsetHeight;
      let left = marker.right + gap;
      if (left + width > window.innerWidth - margin) left = marker.left - gap - width;
      left = Math.max(margin, left);
      const top = Math.min(
        Math.max(margin, marker.top + marker.height / 2 - height / 2),
        window.innerHeight - height - margin
      );
      card.style.left = `${Math.round(left)}px`;
      card.style.top = `${Math.round(top)}px`;
    }
  }

  /* ---------- Film strip: pinned length = sideways distance ---------- */

  class LookbookFilmstrip extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-lb-film-track]');
      this.stage = this.querySelector('.lb-film__stage');
      if (!this.track) return;
      // Photos load after layout; re-measure whenever the track changes size.
      this.resizeObserver = new ResizeObserver(() => measure());
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    onLookbookResize() {
      if (!this.track || !this.stage || !motion()) return;
      const shift = Math.max(0, this.track.scrollWidth - this.stage.clientWidth);
      this.style.setProperty('--shift', `${Math.round(shift)}px`);
    }
  }

  /* ---------- Compare slider ---------- */

  // Touch: a sideways swipe moves the divider by the distance swiped (it
  // never jumps under the finger), an up-or-down swipe is left to scroll the
  // page, a tap glides the divider to the tap, and a flick carries on and
  // slows to a stop. Mouse: press to glide there, then drag. The position is
  // written once a frame as --pos, which only slides layers.
  const COMPARE_SLOP = 8; // px a touch moves before it counts as a swipe
  const COMPARE_FRICTION = 0.985; // fling speed kept per ms
  const COMPARE_MAX_FLING = 0.25; // %/ms

  class LookbookCompare extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('.lb-compare__stage');
      this.range = this.querySelector('.lb-compare__range');
      if (!this.stage || !this.range) return;
      this.pos = 50;
      this.touched = false;
      this.tick = this.tick.bind(this);

      this.range.addEventListener('input', () => {
        this.interrupt();
        this.set(Number(this.range.value));
      });
      this.stage.addEventListener('pointerdown', (event) => this.onDown(event));
      this.stage.addEventListener('pointermove', (event) => this.onMove(event));
      this.stage.addEventListener('pointerup', (event) => this.onEnd(event, true));
      // The browser took the touch to scroll or zoom the page.
      this.stage.addEventListener('pointercancel', (event) => this.onEnd(event, false));
      // A tap, but not the end of a swipe (click is not sent for those, nor
      // for a touch that only stops the page's own scrolling).
      this.stage.addEventListener('click', (event) => {
        if (this.lastPointer === 'mouse') return;
        // A short swipe can still end in a click; it is not a tap.
        if (this.draggedAt && event.timeStamp - this.draggedAt < 400) return;
        this.onTap(event);
      });

      // Once in view, sweep the divider once so it reads as something to drag.
      if (motion()) {
        this.hintObserver = new IntersectionObserver(
          (entries) => {
            if (!entries[0].isIntersecting) return;
            this.hintObserver.disconnect();
            this.hint();
          },
          { threshold: 0.6 }
        );
        this.hintObserver.observe(this.stage);
      }
    }

    disconnectedCallback() {
      if (this.hintObserver) this.hintObserver.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      this.frame = null;
    }

    onDown(event) {
      if (!event.isPrimary || event.button !== 0) return;
      this.lastPointer = event.pointerType;
      // A finger on a flung divider catches it, as on any native list.
      if (this.mode === 'fling') this.mode = null;
      this.gesture = {
        id: event.pointerId,
        touch: event.pointerType !== 'mouse',
        startX: event.clientX,
        startY: event.clientY,
        width: this.stage.getBoundingClientRect().width || 1,
        dragging: false,
        samples: [],
      };
      if (!this.gesture.touch) {
        event.preventDefault();
        this.startDrag(event);
        this.glideTo(this.percentAt(event.clientX), 0.35);
      }
    }

    onMove(event) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;

      if (!gesture.dragging) {
        const dx = Math.abs(event.clientX - gesture.startX);
        const dy = Math.abs(event.clientY - gesture.startY);
        if (dy > COMPARE_SLOP && dy >= dx) {
          // Scrolling the page: leave this touch alone.
          this.gesture = null;
          return;
        }
        if (dx < COMPARE_SLOP || dx < dy) return;
        this.startDrag(event);
      }

      if (gesture.touch) {
        // Follow the finger one to one, from where the swipe took hold.
        this.pos = clamp(gesture.from + ((event.clientX - gesture.originX) / gesture.width) * 100, 0, 100);
        this.mode = null;
        this.schedule();
        gesture.samples.push({ x: event.clientX, t: event.timeStamp });
        if (gesture.samples.length > 8) gesture.samples.shift();
      } else {
        this.glideTo(this.percentAt(event.clientX), 0.35);
      }
    }

    onEnd(event, released) {
      const gesture = this.gesture;
      if (!gesture || event.pointerId !== gesture.id) return;
      this.gesture = null;
      this.classList.remove('is-dragging');
      if (gesture.dragging) this.draggedAt = event.timeStamp;
      if (released && gesture.dragging && gesture.touch) this.fling(gesture, event.timeStamp);
    }

    onTap(event) {
      this.interrupt();
      this.glideTo(this.percentAt(event.clientX), 0.2);
    }

    startDrag(event) {
      const gesture = this.gesture;
      gesture.dragging = true;
      gesture.from = this.pos;
      gesture.originX = event.clientX;
      this.interrupt();
      this.classList.add('is-dragging');
      try {
        this.stage.setPointerCapture(event.pointerId);
      } catch (error) {
        // The pointer is already gone; nothing to hold on to.
      }
    }

    // Carry on in the direction of a flick, slowing to a stop.
    fling(gesture, now) {
      const samples = gesture.samples.filter((sample) => now - sample.t < 100);
      if (samples.length < 2 || now - samples[samples.length - 1].t > 50 || !motion()) return;
      const first = samples[0];
      const last = samples[samples.length - 1];
      const elapsed = last.t - first.t;
      if (elapsed <= 0) return;
      const velocity = ((last.x - first.x) / elapsed / gesture.width) * 100;
      if (Math.abs(velocity) < 0.03) return;
      this.velocity = clamp(velocity, -COMPARE_MAX_FLING, COMPARE_MAX_FLING);
      this.mode = 'fling';
      this.schedule();
    }

    glideTo(percent, rate) {
      this.target = clamp(percent, 0, 100);
      if (!motion()) {
        this.set(this.target);
        return;
      }
      this.rate = rate;
      this.mode = 'glide';
      this.schedule();
    }

    // Stop the opening sweep and any glide or fling: the reader has it now.
    interrupt() {
      this.touched = true;
      this.mode = null;
    }

    percentAt(clientX) {
      const rect = this.stage.getBoundingClientRect();
      return clamp((clientX - rect.left) / (rect.width || 1)) * 100;
    }

    set(percent) {
      this.pos = clamp(percent, 0, 100);
      this.mode = null;
      this.schedule();
    }

    schedule() {
      if (this.frame) return;
      this.last = 0;
      this.frame = requestAnimationFrame(this.tick);
    }

    tick(now) {
      const elapsed = this.last ? Math.min(64, now - this.last) : 16.7;
      this.last = now;

      if (this.mode === 'glide') {
        this.pos += (this.target - this.pos) * (1 - Math.pow(1 - this.rate, elapsed / 16.7));
        if (Math.abs(this.target - this.pos) < 0.05) {
          this.pos = this.target;
          this.mode = null;
        }
      } else if (this.mode === 'fling') {
        this.pos += this.velocity * elapsed;
        this.velocity *= Math.pow(COMPARE_FRICTION, elapsed);
        if (this.pos <= 0 || this.pos >= 100 || Math.abs(this.velocity) < 0.004) this.mode = null;
        this.pos = clamp(this.pos, 0, 100);
      }

      const value = Math.round(this.pos * 100) / 100;
      if (value !== this.rendered) {
        this.rendered = value;
        this.stage.style.setProperty('--pos', `${value}%`);
        this.range.value = String(Math.round(value));
      }

      this.frame = this.mode ? requestAnimationFrame(this.tick) : null;
    }

    hint() {
      const keys = [50, 28, 72, 50];
      const duration = 1800;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      setTimeout(() => {
        const start = performance.now();
        const frame = (now) => {
          if (this.touched) return;
          const t = clamp((now - start) / duration);
          const segment = Math.min(keys.length - 2, Math.floor(t * (keys.length - 1)));
          const local = t * (keys.length - 1) - segment;
          this.set(keys[segment] + (keys[segment + 1] - keys[segment]) * ease(local));
          if (t < 1) requestAnimationFrame(frame);
        };
        requestAnimationFrame(frame);
      }, 300);
    }
  }

  /* ---------- Buy boxes: size pills ---------- */

  document.addEventListener('change', (event) => {
    const pill = event.target.closest('.lb-buy__pill input');
    if (!pill) return;
    const button = pill.form && pill.form.querySelector('.lb-buy__add');
    if (!button) return;
    const available = pill.dataset.available === 'true';
    button.disabled = !available;
    const label = button.querySelector('span');
    if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
  });

  /* ---------- Boot ---------- */

  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };
  define('lookbook-cover', LookbookCover);
  define('lb-contents', LookbookContents);
  define('lb-detail', LookbookDetail);
  define('lb-filmstrip', LookbookFilmstrip);
  define('lb-compare', LookbookCompare);

  // Pick up every reveal and progress element on the page — including ones
  // that arrive later, when the theme editor (or a dev server's hot reload)
  // swaps a section's markup in place.
  const seen = new WeakSet();
  function scan() {
    numberPages();
    document.querySelectorAll('.lb [data-lb-reveal]').forEach((el) => {
      if (seen.has(el)) return;
      seen.add(el);
      if (motion()) revealObserver.observe(el);
      else el.classList.add('is-in');
    });
    tracked.forEach((el) => {
      if (!el.isConnected) tracked.delete(el);
    });
    document.querySelectorAll('[data-lb-progress]').forEach((el) => tracked.add(el));
    measure();
  }

  function boot() {
    scan();
    Rail.build();

    // Only inserted elements that carry LookBook parts matter; text updates
    // (counters, page numbers, button labels) must not trigger a rescan.
    const relevant = (node) =>
      node.nodeType === 1 &&
      (node.matches('[data-lb-reveal], [data-lb-progress]') ||
        node.querySelector('[data-lb-reveal], [data-lb-progress]') !== null);
    let pending = false;
    new MutationObserver((mutations) => {
      if (pending) return;
      if (!mutations.some((mutation) => Array.from(mutation.addedNodes).some(relevant))) return;
      pending = true;
      requestAnimationFrame(() => {
        pending = false;
        scan();
      });
    }).observe(document.getElementById('MainContent') || document.body, { childList: true, subtree: true });

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', measure);
    window.addEventListener('load', measure);

    reduceMotion.addEventListener('change', () => {
      root.classList.toggle('lb-motion', motion());
      measure();
    });
  }

  // Theme editor: show a re-rendered section straight away rather than
  // waiting for it to be scrolled into view again.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-lb-reveal]').forEach((el) => el.classList.add('is-in'));
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
